import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { HStack, Text, IconButton, Spinner, Icon } from "@chakra-ui/react";
import { MdCreditCard, MdVpnKey, MdVisibility, MdVisibilityOff } from "react-icons/md";
import InfoRow from "./InfoRow";
import { maskIban, formatIban } from "./maskUtils";
import { useWith2FA } from "../../../../tools/twoFactor";

/**
 * Reveals the IBAN/SWIFT via the 2FA-protected endpoint and stores the clear
 * values; surfaces a toast on failure other than a user cancel.
 * @param {number} bankId - The banking record id
 * @param {Function} runWith2FA - The 2FA-aware request runner
 * @param {Function} setRevealed - Setter for the revealed { iban, swift }
 * @param {Function} setLoading - Setter for the loading flag
 * @returns {Promise<void>}
 */
async function revealIban(bankId, runWith2FA, setRevealed, setLoading) {
    setLoading(true);
    try {
        const { data } = await runWith2FA({ method: "get", url: `/admin/wallets/${bankId}/iban_reveal` });
        setRevealed({ iban: data?.iban, swift: data?.swift });
    } catch (error) {
        if (error?.message !== "2fa_cancelled") toast.error("Révélation de l'IBAN impossible");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the reveal/mask toggle icon depending on loading and revealed state.
 * @param {boolean} loading - Whether a reveal request is in progress
 * @param {boolean} revealed - Whether the clear value is shown
 * @returns {React.ReactElement}
 */
function toggleIcon(loading, revealed) {
    if (loading) return <Spinner size="sm" boxSize="14px" color="#17dcb8" />;
    if (revealed) return <Icon as={MdVisibilityOff} boxSize="16px" />;
    return <Icon as={MdVisibility} boxSize="16px" />;
}

/**
 * Renders the IBAN value node with a reveal/mask toggle button.
 * @param {Object} props
 * @param {string} props.text - The masked or clear IBAN text to display
 * @param {boolean} props.revealed - Whether the clear value is shown
 * @param {boolean} props.loading - Whether a reveal request is in progress
 * @param {boolean} props.canReveal - Whether a bank id is available
 * @param {Function} props.onToggle - Toggle handler
 * @returns {React.ReactElement}
 */
function IbanValueNode({ text, revealed, loading, canReveal, onToggle }) {
    return (
        <HStack spacing={1} alignItems="center" justifyContent="flex-end">
            <Text color="rgba(255,255,255,0.9)" fontSize="0.75rem" fontFamily="monospace">
                {text}
            </Text>
            {canReveal && (
                <IconButton aria-label="Révéler l'IBAN" size="sm" variant="ghost" onClick={onToggle}
                    isDisabled={loading} color="#17dcb8" minW="auto" h="auto" p="1px"
                    icon={toggleIcon(loading, revealed)} />
            )}
        </HStack>
    );
}

/**
 * IBAN row masked by default with a 2FA-gated "reveal" toggle. Revealing calls
 * the server endpoint (audited as ACCESS_IBAN) and shows the SWIFT as well.
 * @param {Object} props
 * @param {number} props.bankId - The banking record id (required to reveal)
 * @param {string} props.iban - The (possibly clear) IBAN from the bank payload
 * @param {boolean} props.isMobile - Mobile viewport flag
 * @returns {React.ReactElement}
 */
function SensitiveIbanRow({ bankId, iban, isMobile }) {
    const runWith2FA = useWith2FA();
    const [revealed, setRevealed] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => { setRevealed(null); }, [bankId]);

    const text = revealed ? formatIban(revealed.iban) : maskIban(iban);
    const onToggle = revealed
        ? () => setRevealed(null)
        : () => revealIban(bankId, runWith2FA, setRevealed, setLoading);

    return (
        <>
            <InfoRow
                icon={MdCreditCard}
                label="IBAN"
                isMobile={isMobile}
                sx={{ mb: 2 }}
                value={
                    <IbanValueNode
                        text={text}
                        revealed={!!revealed}
                        loading={loading}
                        canReveal={Boolean(bankId)}
                        onToggle={onToggle}
                    />
                }
            />
            {revealed?.swift && (
                <InfoRow icon={MdVpnKey} label="SWIFT" value={revealed.swift} variant="monospace" isMobile={isMobile} sx={{ mb: 2 }} />
            )}
        </>
    );
}

export default SensitiveIbanRow;
